import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, User, FileText, Clock } from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";

interface StageDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage: {
    id: string;
    name: string;
    description: string;
    status: string;
  } | null;
  data?: any;
  candidateName: string;
}

export function StageDetailDialog({ 
  open, 
  onOpenChange, 
  stage, 
  data, 
  candidateName 
}: StageDetailDialogProps) {
  
  // Early return if stage is null
  if (!stage) {
    return null;
  }
  
  const renderStageDetails = () => {
    switch (stage.id) {
      case 'application':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Ημερομηνία Αίτησης</label>
                <p className="text-sm">{data?.application_date ? format(new Date(data.application_date), 'PPP', { locale: el }) : 'Μη διαθέσιμο'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Κατάσταση</label>
                <p className="text-sm">
                  <Badge variant="secondary">{data?.status || 'Εκκρεμεί'}</Badge>
                </p>
              </div>
            </div>
            {data?.notes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Σημειώσεις</label>
                <p className="text-sm mt-1">{data.notes}</p>
              </div>
            )}
          </div>
        );

      case 'first_interview_scheduled':
      case 'second_interview_scheduled':
        const interviewType = stage.id.includes('first') ? 'first' : 'second';
        const interview = data?.interviews?.find((i: any) => i.interview_type === interviewType);
        
        return (
          <div className="space-y-4">
            {interview ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ημερομηνία & Ώρα</label>
                    <p className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(interview.scheduled_date), 'PPP p', { locale: el })}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Κατάσταση</label>
                    <p className="text-sm">
                      <Badge variant={interview.status === 'completed' ? 'default' : 'secondary'}>
                        {interview.status === 'completed' ? 'Ολοκληρωμένη' : 'Προγραμματισμένη'}
                      </Badge>
                    </p>
                  </div>
                </div>
                {interview.interview_data?.location && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Τοποθεσία</label>
                    <p className="text-sm">{interview.interview_data.location}</p>
                  </div>
                )}
                {interview.interview_data?.notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Σημειώσεις</label>
                    <p className="text-sm">{interview.interview_data.notes}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Δεν βρέθηκαν πληροφορίες συνέντευξης</p>
            )}
          </div>
        );

      case 'first_interview':
      case 'second_interview':
        const completedInterviewType = stage.id.includes('first') ? 'first' : 'second';
        const completedInterview = data?.interviews?.find((i: any) => 
          i.interview_type === completedInterviewType && i.status === 'completed'
        );
        
        return (
          <div className="space-y-4">
            {completedInterview ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ημερομηνία Διεξαγωγής</label>
                    <p className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(completedInterview.completed_at || completedInterview.scheduled_date), 'PPP p', { locale: el })}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Κατάσταση</label>
                    <p className="text-sm">
                      <Badge variant="default">Ολοκληρωμένη</Badge>
                    </p>
                  </div>
                </div>
                
                {completedInterview.interview_data && (
                  <>
                    {completedInterview.interview_data.duration && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Διάρκεια</label>
                        <p className="text-sm flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {completedInterview.interview_data.duration}
                        </p>
                      </div>
                    )}
                    
                    {completedInterview.interview_data.location && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Τοποθεσία</label>
                        <p className="text-sm">{completedInterview.interview_data.location}</p>
                      </div>
                    )}
                    
                    {completedInterview.interview_data.questions && completedInterview.interview_data.questions.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Ερωτήσεις & Απαντήσεις</label>
                        <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                          {completedInterview.interview_data.questions.map((q: any, index: number) => (
                            <Card key={index} className="p-3">
                              <p className="text-sm font-medium">{q.question}</p>
                              <p className="text-sm text-muted-foreground mt-1">{q.answer}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {completedInterview.evaluation_notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Σημειώσεις Αξιολόγησης</label>
                    <p className="text-sm mt-1 p-3 bg-muted rounded-md">{completedInterview.evaluation_notes}</p>
                  </div>
                )}
                
                {completedInterview.evaluation_score && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Βαθμολογία</label>
                    <p className="text-sm mt-1 font-semibold">{completedInterview.evaluation_score}/10</p>
                  </div>
                )}
                
                {completedInterview.recommendation && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Σύσταση</label>
                    <p className="text-sm mt-1">
                      <Badge variant={completedInterview.recommendation === 'proceed' ? 'default' : 'destructive'}>
                        {completedInterview.recommendation === 'proceed' ? 'Συνέχεια' : 'Απόρριψη'}
                      </Badge>
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Δεν βρέθηκαν πληροφορίες ολοκληρωμένης συνέντευξης</p>
            )}
          </div>
        );

      case 'first_test':
      case 'second_test':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Τύπος Τεστ</label>
                <p className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {stage.name}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Κατάσταση</label>
                <p className="text-sm">
                  <Badge variant="secondary">Ολοκληρωμένο</Badge>
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Περιγραφή</label>
              <p className="text-sm">{stage.description}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Περιγραφή</label>
              <p className="text-sm">{stage.description}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Κατάσταση</label>
              <p className="text-sm">
                <Badge variant="default">Ολοκληρωμένο</Badge>
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {stage.name} - {candidateName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4">
              {renderStageDetails()}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}